import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { isLoadedSkillNotice } from "../../../shared/skills/attachedSkills";

export function hasPlanModeHistory(
  entries: readonly ChatEntryWithSubTables[]
): boolean {
  return entries.some(
    (entry) =>
      entry.entryType === "UserMessage" && entry.userMessage?.planMode != null
  );
}

export function isGroupChat(
  entries: readonly ChatEntryWithSubTables[]
): boolean {
  const authorIds = new Set<string>();
  for (const entry of entries) {
    const authorId =
      entry.userMessage?.user?.id ?? entry.userMessage?.serviceAccount?.id;
    if (authorId) authorIds.add(authorId);
    if (authorIds.size > 1) return true;
  }
  return false;
}

export function canInterrupt(
  entries: readonly ChatEntryWithSubTables[]
): boolean {
  const lastEntry = entries.findLast((entry) => !isLoadedSkillNotice(entry));
  return (
    lastEntry != null &&
    lastEntry.entryType !== "UserInterrupt" &&
    lastEntry.entryType !== "SystemMessage"
  );
}

export function latestUserMessageBefore(
  entries: readonly ChatEntryWithSubTables[],
  entryId: string
): ChatEntryWithSubTables | undefined {
  const entryIndex = entries.findIndex((entry) => entry.id === entryId);
  const startIndex = entryIndex === -1 ? entries.length - 1 : entryIndex - 1;
  for (let index = startIndex; index >= 0; index--) {
    const entry = entries[index];
    if (entry?.entryType === "UserMessage") return entry;
  }
}

export function latestPendingToolUse(
  entries: readonly ChatEntryWithSubTables[],
  chatId: string,
  accepts: (toolName: string) => boolean
): ChatEntryWithSubTables | undefined {
  for (let index = entries.length - 1; index >= 0; index--) {
    const entry = entries[index];
    if (
      entry?.entryType === "AssistantToolUse" &&
      entry.toolCall &&
      entry.chatId === chatId &&
      entry.toolCall.completedAt == null &&
      accepts(entry.toolCall.toolName)
    ) {
      return entry;
    }
  }
}

export function latestToolMessage<T extends { role: string }>(
  messages: ReadonlyMap<string, T>
): T | undefined {
  // Older tool messages remain in the streaming map, so the last tool must win.
  let latest: T | undefined;
  for (const message of messages.values()) {
    if (message.role === "tool") latest = message;
  }
  return latest;
}

export function latestToolStep(
  messages: ReadonlyMap<string, { role: string; toolInput?: unknown }>
): string | undefined {
  // Keep showing the last known step until a newer tool preview supplies its own.
  let latest: string | undefined;
  for (const message of messages.values()) {
    if (message.role !== "tool") continue;
    const step = (message.toolInput as { step?: string } | undefined)?.step;
    if (step) latest = step;
  }
  return latest;
}
